/**
 * A filter that admits more than one answer.
 *
 * A `<select>` asks *which one*, and a trail is almost never read that way:
 * "what did these two people do to those three workspaces" is one question, and
 * a control that holds one value makes somebody ask it six times and add the
 * answers up in their head. So the control keeps a set — and says so on its
 * face, because a filter whose state is hidden behind a click is a filter
 * people forget they left on.
 *
 * Three rules, and each is there because the single-value control implied it:
 *
 * - **Empty means everything.** Clearing the last option is not an empty
 *   result, it is no filter at all — the same answer the page opens with.
 * - **The face names the choice.** One chosen shows its label; several show a
 *   count; none shows the word for all of them.
 * - **Nothing is applied on close.** Each tick is the filter changing, so the
 *   list under it moves while the menu is open and the effect of a choice is
 *   visible at the moment it is made.
 */
import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { color, font, radius } from '../theme/tokens';
import { ChevronDown } from './icons';

export interface Choice {
  value: string;
  label: string;
  /** A second line under the label — an address under a name, say. */
  meta?: string;
}

export function MultiSelect({
  label,
  /** The word for "no filter at all": `Every project`, `Everybody`. */
  all,
  options,
  value,
  onChange,
  width = 190
}: {
  label: string;
  all: string;
  options: Choice[];
  value: string[];
  onChange: (value: string[]) => void;
  width?: number;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const chosen = options.filter((option) => value.includes(option.value));
  const face = chosen.length === 0 ? all : chosen.length === 1 ? chosen[0].label : `${chosen.length} ${label.toLowerCase()}`;

  const toggle = (option: string): void =>
    onChange(value.includes(option) ? value.filter((one) => one !== option) : [...value, option]);

  return (
    <div ref={ref} style={{ position: 'relative', flex: 'none' }}>
      <button
        type="button"
        className="hdr-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((on) => !on)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width,
          height: 32,
          padding: '0 10px',
          border: `1px solid ${chosen.length ? color.ink : color.lineInput}`,
          borderRadius: radius.md,
          background: color.surface,
          fontFamily: 'inherit',
          fontSize: 13,
          color: chosen.length ? color.ink : color.inkSubtle,
          cursor: 'pointer'
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
          {face}
        </span>
        <span style={{ flex: 'none', display: 'flex', color: color.inkFaint }}>
          <ChevronDown open={open} />
        </span>
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable="true"
          style={{
            position: 'absolute',
            top: 38,
            left: 0,
            zIndex: 30,
            width: Math.max(width, 240),
            maxHeight: 320,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            padding: 5,
            border: `1px solid ${color.line}`,
            borderRadius: 10,
            background: color.surface,
            boxShadow: '0 1px 2px rgba(22,25,31,.06), 0 18px 40px -14px rgba(22,25,31,.22)'
          }}
        >
          {/* The way back to "everything", which is otherwise a matter of
              remembering which ones were ticked. */}
          <button
            type="button"
            className="me-item"
            onClick={() => onChange([])}
            style={{ ...item, color: chosen.length ? color.accentText : color.inkFaint }}
          >
            {all}
          </button>
          {options.length > 0 && <span style={{ height: 1, margin: '3px 4px', background: color.hairSoft }} />}
          {options.length === 0 && (
            <span style={{ padding: '7px 10px', fontSize: 12.5, color: color.inkFaint }}>Nothing to filter by yet.</span>
          )}
          {options.map((option) => {
            const on = value.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={on}
                className="me-item"
                onClick={() => toggle(option.value)}
                style={{ ...item, alignItems: 'flex-start', height: 'auto', padding: '7px 10px' }}
              >
                <Tick on={on} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 13,
                      color: color.ink,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {option.label}
                  </span>
                  {option.meta && (
                    <span style={{ display: 'block', fontFamily: font.mono, fontSize: 11, color: color.inkFaint, marginTop: 2 }}>
                      {option.meta}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

const item: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 9,
  width: '100%',
  height: 30,
  padding: '0 10px',
  borderRadius: 7,
  border: 0,
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: 13,
  color: color.inkBody
};

/** A box, and a mark in it. Ink when it is on — the one fill this control has. */
function Tick({ on }: { on: boolean }): React.JSX.Element {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 14,
        height: 14,
        marginTop: 2,
        border: `1px solid ${on ? color.ink : color.lineInput}`,
        borderRadius: 4,
        background: on ? color.ink : 'transparent',
        color: '#FFFFFF'
      }}
    >
      {on && (
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round">
          <path d="m5 12.5 4.5 4.5L19 7.5" />
        </svg>
      )}
    </span>
  );
}
